import { REACTIONS, ReactionType } from "@/src/models/post.model";
import ReactionGlyph from "@/src/components/posts/reactions/reaction-glyph";
import { animateBounce } from "@/src/utils/animation.utils";
import { ArrowForwardSquare, Bookmark2, MessageText, Sticker } from "iconsax-react-nativejs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Animated, Platform, Pressable, Text, View } from "react-native";
import { useTheme } from "@/src/styles/theme-provider";
import ReactionPicker from "../reactions/reaction-picker";
import { LikeButton } from "./bottom-buttons/bottom-buttons";

type Props = Readonly<{
  userReaction?: ReactionType;
  reactionCounts?: Partial<Record<Exclude<ReactionType, null>, number>>;
  reactionTotal?: number;
  commentCount?: number;
  saveCount?: number;
  onReact?: (reaction: ReactionType | null) => void;
  onComment?: () => void;
  onShare?: () => void;
  onSave?: () => void;
  isSaved?: boolean;
  onReactorCount?: () => void;
  /** Sticker mode; the button only shows when provided (club-stickers-v1 configured). */
  onSticker?: () => void;
  stickerCount?: number;
  hasSticker?: boolean;
  /** The post is currently in sticker mode (button shows pressed). */
  stickerActive?: boolean;
}>;

const PostActions = ({
  userReaction = null,
  reactionCounts = {},
  reactionTotal = 0,
  commentCount = 0,
  saveCount = 0,
  onReact,
  onComment,
  onShare,
  onSave,
  isSaved = false,
  onReactorCount,
  onSticker,
  stickerCount = 0,
  hasSticker = false,
  stickerActive = false,
}: Props) => {
  const { themeVars } = useTheme();
  const { t } = useTranslation();

  const [showPicker, setShowPicker] = useState(false);
  const [isReactorHovered, setIsReactorHovered] = useState(false);
  const [currentReaction, setCurrentReaction] =
    useState<ReactionType>(userReaction);

  useEffect(() => {
    setCurrentReaction(userReaction);
  }, [userReaction]);

  const likeAnim = useRef(new Animated.Value(1)).current;
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    };
  }, []);

  const topReactions = useMemo(
    () =>
      Object.entries(reactionCounts)
        .filter(([, count]) => (count ?? 0) > 0)
        .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0))
        .slice(0, 3)
        .map(([id]) => REACTIONS.find((r) => r.id === id))
        .filter(Boolean) as typeof REACTIONS,
    [reactionCounts],
  );

  const handleLikePress = useCallback(() => {
    animateBounce(likeAnim);
    const reaction = currentReaction ? null : "like";
    setCurrentReaction(reaction);
    onReact?.(reaction);
  }, [currentReaction, likeAnim, onReact]);

  const handlePickerSelect = useCallback(
    (reaction: ReactionType) => {
      animateBounce(likeAnim);
      setCurrentReaction(reaction);
      setShowPicker(false);
      onReact?.(reaction);
    },
    [likeAnim, onReact],
  );

  const handleHoverIn = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setShowPicker(true), 250);
  }, []);

  const handleHoverOut = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setShowPicker(false), 300);
  }, []);

  return (
    <View className="border-ash-3">
      {/* Stats row */}
      <View testID="post-stats-row" className="flex-row justify-between items-center px-6 py-2">
        {/* The stacked emoji each carry a `bg-chalk` ring so they read as a
            pile, which painted over the row's hover background and left it
            visible only under the count on the right. Tracking hover here lets
            the rings adopt the same colour, so the whole control highlights as
            one pill — with padding, so there is a background to see. */}
        <Pressable
          className={`flex-row items-center gap-1 self-start -mx-2 px-2 py-1.5 rounded-lg ${
            isReactorHovered ? "bg-ash-3" : ""
          }`}
          onHoverIn={() => setIsReactorHovered(true)}
          onHoverOut={() => setIsReactorHovered(false)}
          onPress={onReactorCount}
        >
          {topReactions.map((rxn, index) => (
            <ReactionGlyph
              key={rxn.id}
              id={rxn.id}
              size={18}
              style={{
                marginLeft: index === 0 ? 0 : -6,
                zIndex: topReactions.length - index,
              }}
              className={`rounded-full ${isReactorHovered ? "bg-ash-3" : "bg-chalk"}`}
            />
          ))}
          <Text className="text-sm text-ash-2">
            {reactionTotal === 0 ? `0 ${t("LABEL_LIKES")}` : reactionTotal}
          </Text>
        </Pressable>
        <Text className="text-onyx text-xs">
          {commentCount}{" "}
          {commentCount === 1 ? t("LABEL_COMMENT") : t("LABEL_COMMENTS")} ·{" "}
          {saveCount} {saveCount === 1 ? t("LABEL_SAVE") : t("LABEL_SAVES")}
          {onSticker ? (
            <Text testID="post-sticker-count">
              {" "}· {stickerCount} {stickerCount === 1 ? t("LABEL_STICKER") : t("LABEL_STICKERS")}
            </Text>
          ) : null}
        </Text>
      </View>

      {/* Action buttons row */}
      <View testID="post-action-row" className="flex-row flex-wrap justify-start gap-x-4 gap-y-1 px-4 py-3 border-t border-ash-3 md:gap-6 md:px-6">
        <LikeButton
          currentReaction={currentReaction}
          likeAnim={likeAnim}
          onPress={handleLikePress}
          onLongPress={() => {
            if (Platform.OS !== "web") setShowPicker(true);
          }}
          onHoverIn={() => {
            if (Platform.OS === "web") handleHoverIn();
          }}
          onHoverOut={() => {
            if (Platform.OS === "web") handleHoverOut();
          }}
        />

        <Pressable
          className="p-2 rounded hover:bg-ash-3 flex-row items-center gap-2"
          onPress={onComment}
        >
          <MessageText size={18} color={themeVars["--color-slate-default"] as string} variant="Outline" />
          <Text className="text-sm text-ash-2">{t("LABEL_COMMENT")}</Text>
        </Pressable>

        <Pressable
          className="p-2 rounded hover:bg-ash-3 flex-row items-center gap-2"
          onPress={onShare}
        >
          <ArrowForwardSquare
            size={18}
            color={themeVars["--color-slate-default"] as string}
            variant="Outline"
          />
          <Text className="text-sm text-ash-2">{t("LABEL_SHARE")}</Text>
        </Pressable>

        <Pressable
          testID="save-button"
          className="p-2 rounded hover:bg-ash-3 flex-row items-center gap-2"
          onPress={onSave}
        >
          <Bookmark2
            size={18}
            color={
              isSaved
                ? (themeVars["--color-primary-default"] as string)
                : (themeVars["--color-slate-default"] as string)
            }
            variant={isSaved ? "Bold" : "Outline"}
          />
          <Text
            className={`text-sm ${isSaved ? "text-primary" : "text-ash-2"}`}
          >
            {t(isSaved ? "LABEL_UNSAVE" : "LABEL_SAVE")}
          </Text>
        </Pressable>

        {onSticker ? (
          <Pressable
            testID="sticker-button"
            className={`p-2 rounded hover:bg-ash-3 flex-row items-center gap-2 ${stickerActive ? "bg-primary-2" : ""}`}
            onPress={onSticker}
            accessibilityRole="button"
            accessibilityState={{ selected: stickerActive }}
          >
            <Sticker
              size={18}
              color={
                hasSticker
                  ? (themeVars["--color-primary-default"] as string)
                  : (themeVars["--color-slate-default"] as string)
              }
              variant={hasSticker ? "Bold" : "Outline"}
            />
            <Text className={`text-sm ${hasSticker ? "text-primary" : "text-ash-2"}`}>
              {t("LABEL_STICKER_MODE")}
            </Text>
          </Pressable>
        ) : null}
      </View>

      {showPicker && (
        <ReactionPicker
          onSelect={handlePickerSelect}
          onClose={() => setShowPicker(false)}
          onStay={() => {
            if (hoverTimeout.current) {
              clearTimeout(hoverTimeout.current);
              hoverTimeout.current = null;
            }
          }}
        />
      )}
    </View>
  );
};

export default PostActions;
